import range from "lodash/range";
import { FIELD_MARKED_FOR_REMOVAL_KEY } from "PFApp/constants/activity_form/activity_form_field_keys";
import { TemplateKey } from "PFApp/constants/templates";
import { useTemplate } from "PFApp/hooks";
import { useFormValidation } from "PFApp/hooks/activity_form/use_form_validation";
import {
  ActivityFormType,
  FormSchemaField,
  RolesFormKey
} from "PFApp/types/activity_form/activity_form_schema";
import { Subtemplate } from "PFCore/types";
import { Fragment, useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { getRoleFormKeyPrefix } from "../tabular_roles_form.utils";

type UseHiddenFormControllersProps = {
  formSchemaFields: FormSchemaField[];
  rolesCount: number;
  rolesFormKey: RolesFormKey;
  subtemplate: Subtemplate | null;
  skipRequiredFieldsValidation?: boolean;
};

/*
We need to render hidden controllers with validation rules for each field in each role, because table rows
are unmounted when they are not currently visible on the screen (performance optimization),
so the validation wouldn't work for the unmounted rows.
*/
export const useHiddenFormControllers = ({
  formSchemaFields,
  rolesCount,
  rolesFormKey,
  subtemplate,
  skipRequiredFieldsValidation
}: UseHiddenFormControllersProps) => {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const roleTemplate = useTemplate(TemplateKey.Role)!;
  const { isFieldRequired } = useFormValidation({
    subtemplate,
    template: roleTemplate,
    skipRequiredFieldsValidation
  });
  const rolesMarkedForRemovalMap = useWatch({
    name: rolesFormKey,
    compute: (roles = []) =>
      roles.reduce((acc: Record<number, boolean>, role: ActivityFormType, roleIndex: number) => {
        acc[roleIndex] = !!role?.[FIELD_MARKED_FOR_REMOVAL_KEY];
        return acc;
      }, {})
  });

  return useMemo(
    () =>
      formSchemaFields.map((schemaField) => {
        const prefix = getRoleFormKeyPrefix(schemaField);
        const required = isFieldRequired(schemaField);

        return range(rolesCount).map((roleIndex) => {
          const fieldName = `${rolesFormKey}.${roleIndex}.${prefix}${schemaField.name}`;
          const isMarkedForRemoval = !!rolesMarkedForRemovalMap[roleIndex];

          return (
            <Controller
              key={fieldName}
              name={fieldName}
              control={control}
              rules={{
                required: required && !isMarkedForRemoval ? t("cantBeBlank") : false
              }}
              render={() => <Fragment />}
            />
          );
        });
      }),
    [formSchemaFields, isFieldRequired, rolesCount, rolesFormKey, control, t, rolesMarkedForRemovalMap]
  );
};
